<template>
  <section class="settings">
    <!-- API Keys Section -->
    <div class="settings-section">
      <div class="settings-section__header">
        <div class="settings-section__info">
          <div class="settings-section__icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.9167 8.33333L17.5 3.75M17.5 3.75L14.1667 3.75M17.5 3.75V7.08333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8.75 11.25C8.75 12.6307 7.63071 13.75 6.25 13.75C4.86929 13.75 3.75 12.6307 3.75 11.25C3.75 9.86929 4.86929 8.75 6.25 8.75C7.63071 8.75 8.75 9.86929 8.75 11.25Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M6.25 13.75V17.5M6.25 17.5H8.75M6.25 17.5H3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 class="settings-section__title">Test API Keys</h2>
            <p class="settings-section__subtitle">Copy and generate test API keys for your integration</p>
          </div>
        </div>
        <button 
          class="btn-primary"
          @click="createApiKey"
          :disabled="loading"
          v-can.disable="'api:update'"
        >
          <svg v-if="loading && loadingButton === 'apiKey'" class="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" stroke-opacity="0.3"/>
            <path d="M14 8C14 4.68629 11.3137 2 8 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3.33337V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.33325 8H12.6666" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Generate New Key
        </button>
      </div>
      
      <div class="api-key-display" v-if="apiSecret">
        <div class="api-key-display__label">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3333 7.33337H2.66667C1.93029 7.33337 1.33334 7.93033 1.33334 8.66671V13.3334C1.33334 14.0698 1.93029 14.6667 2.66667 14.6667H13.3333C14.0697 14.6667 14.6667 14.0698 14.6667 13.3334V8.66671C14.6667 7.93033 14.0697 7.33337 13.3333 7.33337Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4.66666 7.33337V4.66671C4.66666 3.78265 5.01785 2.93481 5.64297 2.30968C6.26809 1.68456 7.11593 1.33337 7.99999 1.33337C8.88405 1.33337 9.7319 1.68456 10.357 2.30968C10.9821 2.93481 11.3333 3.78265 11.3333 4.66671V7.33337" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Secret Key
        </div>
        <div class="api-key-display__value">
          <input
            class="api-key-display__input"
            type="text"
            name="secretKey"
            id="secretKey"
            ref="secretKey"
            readonly
            v-model="apiSecret"
            @focus="$event.target.select()"
          />
        </div>
        <button class="btn-copy" @click="copyKey">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="5.33334" y="5.33337" width="9.33333" height="9.33333" rx="2" stroke="currentColor" stroke-width="1.5"/>
            <path d="M10.6667 5.33337V4.00004C10.6667 2.89547 9.7712 2.00004 8.66667 2.00004H4.00001C2.89544 2.00004 2.00001 2.89547 2.00001 4.00004V8.66671C2.00001 9.77128 2.89544 10.6667 4.00001 10.6667H5.33334" stroke="currentColor" stroke-width="1.5"/>
          </svg>
          Copy
        </button>
      </div>
      
      <div class="api-key-empty" v-else>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 8V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p>No API key generated yet. Click the button above to generate one.</p>
      </div>
    </div>
    
    <!-- Webhooks Section -->
    <div class="settings-section">
      <div class="settings-section__info">
        <div class="settings-section__icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2.5 10H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 2.5C12.0711 4.73835 13.2274 7.30519 13.3333 10C13.2274 12.6948 12.0711 15.2617 10 17.5C7.92893 15.2617 6.77262 12.6948 6.66667 10C6.77262 7.30519 7.92893 4.73835 10 2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div>
          <h2 class="settings-section__title">Webhooks</h2>
          <p class="settings-section__subtitle">Configure webhook endpoints to receive real-time event notifications</p>
        </div>
      </div>
      
      <form @submit.prevent="performWebhookAction" class="webhook-form">
        <div class="form-group">
          <label for="webhook" class="form-label">Webhook URL</label>
          <input
            type="text"
            class="form-input"
            placeholder="https://your-domain.com/webhook"
            name="url"
            v-model="url"
            id="webhook"
          />
          <p class="form-hint">We'll send POST requests to this URL when events occur</p>
        </div>
        <div class="api-key-display" v-if="webhookSecret">
          <div class="api-key-display__label">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.3333 7.33337H2.66667C1.93029 7.33337 1.33334 7.93033 1.33334 8.66671V13.3334C1.33334 14.0698 1.93029 14.6667 2.66667 14.6667H13.3333C14.0697 14.6667 14.6667 14.0698 14.6667 13.3334V8.66671C14.6667 7.93033 14.0697 7.33337 13.3333 7.33337Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.66666 7.33337V4.66671C4.66666 3.78265 5.01785 2.93481 5.64297 2.30968C6.26809 1.68456 7.11593 1.33337 7.99999 1.33337C8.88405 1.33337 9.7319 1.68456 10.357 2.30968C10.9821 2.93481 11.3333 3.78265 11.3333 4.66671V7.33337" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Signing Key
          </div>
          <div class="api-key-display__value">
            <input
              class="api-key-display__input"
              type="text"
              name="signingKey"
              id="signingKey"
              ref="signingKey"
              readonly
              v-model="webhookSecret"
              @focus="$event.target.select()"
            />
          </div>
          <button class="btn-copy" type="button" @click="copySigningKey">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="5.33334" y="5.33337" width="9.33333" height="9.33333" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <path d="M10.6667 5.33337V4.00004C10.6667 2.89547 9.7712 2.00004 8.66667 2.00004H4.00001C2.89544 2.00004 2.00001 2.89547 2.00001 4.00004V8.66671C2.00001 9.77128 2.89544 10.6667 4.00001 10.6667H5.33334" stroke="currentColor" stroke-width="1.5"/>
            </svg>
            Copy
          </button>
        </div>
        <div class="webhook-form__actions">
          <button 
            type="submit"
            class="btn-primary"
            :disabled="!url || url.length === 0 || loading"
            v-can.disable="'api:update'"
          >
            <svg v-if="loading && loadingButton === 'webhook'" class="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" stroke-opacity="0.3"/>
              <path d="M14 8C14 4.68629 11.3137 2 8 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            {{ webhookButtonText }}
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
export default {
  name: "ApiSettings",
  data() {
    return {
      url: null,
      loadingButton: "",
    };
  },
  created() {
    this.fetchSettingsInfo();
  },
  watch: {
    webhookUrl(val) {
      this.url = val;
    },
  },
  mounted() {
    this.url = this.webhookUrl;
  },
  computed: {
    ...mapGetters("ApiClient", ["webhook", "apiKeyInfo", "loading"]),
    ...mapGetters("organisationModule", ["organisationDetails"]),
    apiSecret() {
      return this.apiKeyInfo?.secret;
    },
    webhookUrl() {
      return this.webhook?.url;
    },
    webhookSecret() {
      return this.webhook?.secret;
    },
    webhookButtonText() {
      let text = null;
      switch (this.webhook) {
        case null:
          text = "Create Webhook";
          break;

        default:
          text = "Save Changes";
          break;
      }
      return text;
    },
  },
  methods: {
    ...mapActions("ApiClient", [
      "getApikey",
      "getApiWebHook",
      "generateApikey",
      "addWebHook",
      "updateWebhook",
    ]),
    ...mapActions(["showToast"]),
    async fetchSettingsInfo() {
      try {
        await Promise.all([this.getApikey(), this.getApiWebHook()]);
      } catch (error) {
        return error;
      }
    },

    async createApiKey() {
      this.loadingButton = "apiKey";
      try {
        console.log({ live: this.organisationDetails.live });
        const response = await this.generateApikey(
          this.organisationDetails.live
        );
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
      } catch (error) {
        return error;
      }
    },
    copyKey() {
      this.$refs.secretKey.focus();
      document.execCommand("copy");
      this.showToast({
        description: "Secret key copied",
        display: true,
        type: "success",
      });
    },
    copySigningKey() {
      this.$refs.signingKey.focus();
      document.execCommand("copy");
      this.showToast({
        description: "Signing key copied",
        display: true,
        type: "success",
      });
    },
    performWebhookAction() {
      this.loadingButton = "webhook";
      switch (this.webhook) {
        case null:
          this.createWebhook();
          break;
        default:
          this.changeWebhook();
          break;
      }
    },
    async createWebhook() {
      try {
        const response = await this.addWebHook({ 
          url: this.url,
          live: this.organisationDetails.live 
        });
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
      } catch (error) {
        return error;
      }
    },
    async changeWebhook() {
      try {
        const response = await this.updateWebhook({ 
          url: this.url,
          live: this.organisationDetails.live 
        });
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
      } catch (error) {
        return error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.settings {
  padding: 32px;
}

.settings-section {
  background: #F8FAFC;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }
  
  &__info {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 20px;
  }
  
  &__icon {
    width: 40px;
    height: 40px;
    background: #EEF4FF;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4D84FF;
    flex-shrink: 0;
  }
  
  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }
  
  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }
}

.api-key-display {
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  overflow: hidden;
  
  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px;
    background: #F8FAFC;
    border-right: 1px solid #E2E8F0;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
    white-space: nowrap;
    
    svg {
      color: #64748B;
    }
  }
  
  &__value {
    flex: 1;
    display: flex;
    align-items: center;
  }
  
  &__input {
    width: 100%;
    padding: 14px 16px;
    border: none;
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    
    &:focus {
      outline: none;
    }
  }
}

.api-key-empty {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #FEF9C3;
  border-radius: 10px;
  
  svg {
    color: #CA8A04;
    flex-shrink: 0;
  }
  
  p {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #854D0E;
    margin: 0;
  }
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: #4D84FF;
  border: none;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    background: #3B6FE8;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-copy {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 16px;
  background: #0F172A;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #1E293B;
  }
}

.webhook-form {
  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }
}

.form-group {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #0F172A;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #94A3B8;
  }
  
  &:focus {
    outline: none;
    border-color: #4D84FF;
    box-shadow: 0 0 0 3px rgba(77, 132, 255, 0.1);
  }
}

.form-hint {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #64748B;
  margin: 8px 0 0 0;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .settings {
    padding: 20px;
  }
  
  .settings-section__header {
    flex-direction: column;
    
    .btn-primary {
      width: 100%;
    }
  }
  
  .api-key-display {
    flex-direction: column;
    
    &__label {
      border-right: none;
      border-bottom: 1px solid #E2E8F0;
    }
  }
  
  .btn-copy {
    width: 100%;
    justify-content: center;
  }
}
</style>
